/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */
import {inject, Pipe, PipeTransform} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

export interface SchemaVersionInput {
  type?: string;
  standardVersion?: string;
  version?: string;
}

/** Translation key for the "Longlist" label used in battery schema version strings. */
export const LONGLIST_I18N_KEY = 'battery_pass.list.longlist';

/**
 * Returns a formatted version label string for a schema.
 *
 * - Battery schemas with `standardVersion`:
 *   - Multiple battery schemas in `allSchemas`: `v{standardVersion}/{version}` (disambiguates)
 *   - Single battery schema in `allSchemas`: `v{standardVersion}` (no version suffix needed)
 *   - No `allSchemas` provided: `{longlistLabel} v{standardVersion}/{version}`
 * - Battery schemas without `standardVersion`: `v{version}`
 * - All other schema types: `v{standardVersion} / {version}` or `v{version}`
 */
export function getSchemaVersionLabel(
  schema: SchemaVersionInput,
  allSchemas?: SchemaVersionInput[],
  longlistLabel = 'Longlist',
): string {
  if (schema.type === 'battery') {
    if (!schema.standardVersion) {
      return `v${schema.version ?? ''}`;
    }
    if (allSchemas) {
      const batteryCount = allSchemas.filter(
        (s) => s.type === 'battery',
      ).length;
      return batteryCount > 1
        ? `v${schema.standardVersion}/${schema.version}`
        : `v${schema.standardVersion}`;
    }
    return `${longlistLabel} v${schema.standardVersion}/${schema.version}`;
  }
  return schema.standardVersion
    ? `v${schema.standardVersion} / ${schema.version}`
    : `v${schema.version ?? ''}`;
}

@Pipe({name: 'schemaVersionLabel', standalone: false})
export class SchemaVersionLabelPipe implements PipeTransform {
  private readonly translate = inject(TranslateService);

  transform(
    schema: SchemaVersionInput,
    allSchemas?: SchemaVersionInput[],
  ): string {
    return getSchemaVersionLabel(
      schema,
      allSchemas,
      this.translate.instant(LONGLIST_I18N_KEY),
    );
  }
}
